/**
 * "About this card" — what each dashboard card shows, how it is worked out,
 * its edge cases and quirks, and where its numbers come from. Every line is
 * the backend's own rule (VentaWiz-Backend, paths in the comments), so a
 * change there is a change here: a help panel that disagrees with the code is
 * worse than none.
 *
 * Each card: `title`, then five lists — shows, calculation, edgeCases,
 * quirks, source. The panel renders whatever is in them.
 */
export const dashboardInfo = {
  sections: {
    shows: 'What it shows',
    calculation: 'How it’s calculated',
    edgeCases: 'Edge cases',
    quirks: 'Quirks & caveats',
    source: 'Where the data comes from',
  },
  open: 'About {{title}}',
  close: 'Close',
  cards: {
    // reports/areas/sales.py · analytics int_mp_order_lines.sql
    sales: {
      title: 'Sales',
      shows: [
        'What your marketplace orders were worth in the window, in the currency the section shows.',
      ],
      calculation: [
        'Each order line: price (unit price × quantity, or the line price, as the marketplace reports it) + shipping − discount. Tax is kept apart and not included.',
        'Lines are dated by the day the order was placed, in the marketplace’s own time zone.',
        'Other currencies are converted by the backend at the latest stored exchange rate.',
      ],
      edgeCases: [
        'Cancelled lines and duplicate copies of a line count as 0.',
        'Returned or refunded lines stay in sales — returns are reported separately, never subtracted.',
        'A currency with no exchange rate is left out of the total, and the note under the figures names it.',
      ],
      quirks: [
        'Seller (marketplace) orders only: vendor (VO) and direct-fulfilment (DF) invoices are not here. The forecast, growth and flow include them, so their totals are larger.',
        'Past months are converted at today’s rate, so a closed month’s total can move as rates move.',
      ],
      source: [
        'The marketplace sales mart (one row per order line), rebuilt from the ingested orders.',
      ],
    },
    units: {
      title: 'Units',
      shows: ['How many units your marketplace orders held in the window.'],
      calculation: [
        'The quantity on every order line placed in the window, by the marketplace’s local order day.',
      ],
      edgeCases: [
        'Cancelled and duplicate lines count 0 units.',
        'Returned units are still counted.',
      ],
      quirks: [
        'A count, so it is the same in every currency: stores in every currency are added together.',
        'Seller orders only — vendor and DF units are not here.',
      ],
      source: ['The marketplace sales mart (one row per order line).'],
    },
    // analytics mart_ads_daily.sql · reports/areas/listing_data.py
    ad_spend: {
      title: 'Ad spend',
      shows: ['What your Amazon ads cost in the window.'],
      calculation: [
        'The sum of spend over every ad, day by day, plus each ad group’s remainder (the part Amazon reports for the group but not per ad), so the total matches Amazon’s ad-group report.',
        'Dated by the ads profile’s own day.',
      ],
      edgeCases: [
        'On a day the ad-group report is missing, only product ads are counted — a lower bound with no Sponsored Brands spend.',
        'Some days are estimates built from Amazon’s hourly stream.',
      ],
      quirks: [
        'Sponsored Brands spend sits only in the ad-group remainder, which has no SKU — so filtering by brand drops it.',
        'Each ad’s currency is its store’s marketplace currency, then converted to the currency shown.',
      ],
      source: ['The daily ads mart (one row per ad per day, plus a remainder per ad group).'],
    },
    ad_sales: {
      title: 'Ad sales',
      shows: ['Sales Amazon attributes to your ads in the window.'],
      calculation: [
        'The sum of attributed sales over every ad and ad-group remainder, by the ads profile’s day.',
      ],
      edgeCases: [
        'Days with no ad-group report count only product ads.',
        'Amazon’s attribution keeps updating for a few days, so the last days can still rise.',
      ],
      quirks: [
        'This is Amazon’s attribution, not your order lines: it is not a share of Sales and can include orders from other stores’ listings.',
        'A brand filter drops Sponsored Brands sales.',
      ],
      source: ['The daily ads mart.'],
    },
    acos: {
      title: 'ACOS',
      shows: [
        'Advertising cost of sales: how much ad spend each unit of ad sales cost. Lower is better.',
      ],
      calculation: [
        'Total ad spend ÷ total ad sales over the window — worked out from the sums, never an average of daily ACOS.',
        'When amounts are converted, ACOS is worked out again from the converted sums.',
      ],
      edgeCases: [
        'No ad sales means no ACOS: the card shows nothing rather than an infinite value.',
      ],
      quirks: [
        'The same rules as Ad spend and Ad sales apply: a brand filter drops Sponsored Brands.',
      ],
      source: ['The daily ads mart.'],
    },
    clicks: {
      title: 'Ad clicks',
      shows: ['How many times your ads were clicked in the window.'],
      calculation: [
        'The sum of clicks over every ad and ad-group remainder, by the ads profile’s day.',
      ],
      edgeCases: ['Days with no ad-group report count only product-ad clicks.'],
      quirks: ['A count, so stores in every currency are added together.'],
      source: ['The daily ads mart.'],
    },
    // The main chart's four views.
    trend: {
      title: 'Over time',
      shows: ['The figure picked in the row above, day by day, week by week or month by month.'],
      calculation: [
        'The same report as the figure’s card, grouped by period. Weeks start on Sunday; months are calendar months.',
      ],
      edgeCases: [
        'A period with no data is drawn as a gap, not a zero.',
        'The first and last week or month can be partial: they are cut at the window’s edges.',
      ],
      quirks: ['Counts (units, clicks) add every currency; money is in the currency shown.'],
      source: [
        'The marketplace sales mart or the daily ads mart, whichever the figure comes from.',
      ],
    },
    // dashboard/forecast.py · dashboard/service.py
    forecast: {
      title: 'Forecast',
      shows: [
        'Where the week or month is heading for sales, ad spend or ad sales: the days so far, the days ahead, and a likely range.',
      ],
      calculation: [
        'Trained on the 365 days up to yesterday, with days that had nothing counted as 0.',
        'Forecast for a day = the last 28 days’ average × that weekday’s usual share (over 12 weeks) × a trend from the last 28 days against the 28 before, held to ±1% a day. Never below 0.',
        'Projected total = the period so far (today’s partial day included) + the forecast for the rest of it.',
        'The shaded range is about 90% likely; it widens the further ahead it goes.',
        'Confidence = 1 − the forecast’s average error when the same model was run from four earlier dates.',
      ],
      edgeCases: [
        'Weekly ends on the coming Sunday. On a Sunday it jumps to the next week, so “so far” is 0.',
        '“Not enough history” appears only when the whole year is 0 or nothing is in scope.',
        'Confidence is empty when the backtests had nothing to compare against.',
      ],
      quirks: [
        'Sales here include vendor and DF invoices (by invoice date), so the forecast is not the Sales card’s total.',
        'Brand and SKU filters do not narrow the ad figures.',
        'Units, clicks and ACOS have no forecast.',
      ],
      source: [
        'Sales: the order lines and the vendor and DF invoices. Ads: the ad-group daily data as ingested, not the ads mart.',
      ],
    },
    growth: {
      title: 'Growth',
      shows: [
        'Month-over-month growth of the figure, from January last year to the end of the financial year (31 March).',
      ],
      calculation: [
        'Growth = the month’s total ÷ the month before − 1.',
        'The current and later months are projected: what happened so far + the forecast (with its trend set to 0).',
        'The dashed trend is a robust straight line (Theil–Sen) through the finished months’ growth.',
      ],
      edgeCases: [
        'The first month has no growth, nor does any month after a 0 month.',
        'With too little history the months stop at the current one.',
        'The trend needs at least two finished months.',
      ],
      quirks: [
        'It ignores the date range — it always covers the financial year.',
        'Sales here include vendor and DF invoices, as in the forecast.',
      ],
      source: ['The same daily series as the forecast.'],
    },
    groups: {
      title: 'By group',
      shows: [
        'The figure’s groups ranked — by country, channel, brand, category or subcategory — and every group in the table below.',
      ],
      calculation: [
        'The figure’s report, grouped as picked. Shares are of the total over every group shown.',
      ],
      edgeCases: [
        'A row the report could not place reads “Unknown”; a SKU with no brand reads “Unassigned”.',
        'Stock columns are as it is now; warehouse stock cannot be split by country or channel.',
      ],
      quirks: ['ACOS has no share — a ratio is not a part of a whole.'],
      source: ['The sales, ads, Amazon-stock and warehouse-stock reports.'],
    },
    // dashboard/service.py (flow) · dashboard/repository.py
    flow: {
      title: 'Flow',
      shows: [
        'How the figure splits, step by step, from the total down to each store, brand or product category.',
      ],
      calculation: [
        'Category is Seller, Vendor or Direct fulfilment. Each step adds up the step to its right.',
        'For vendor and DF invoices, the retailer stands in as the “marketplace” and the warehouse as the “store”.',
      ],
      edgeCases: [
        'Brands and product categories beyond the top 80% of their parent fold into “Other (n)”.',
        'A missing brand or category reads “Unassigned”; ads have no product category.',
        'Anything worth ≤ 0.005 is left out.',
      ],
      quirks: [
        'Ad figures can only be split by marketplace, store and brand.',
        'A store or marketplace filter keeps only invoices from retailers linked to those stores.',
        'Sales include vendor and DF invoices, so the total is larger than the Sales card.',
      ],
      source: ['The order lines, the vendor and DF invoices, and the ad-group daily data.'],
    },
    destinations: {
      title: 'Sales by country',
      shows: [
        'The window’s marketplace orders by the country they ship to, on a map and in a table.',
      ],
      calculation: [
        'Orders = distinct orders; units = the units on their lines; cancelled lines left out.',
        'The country is the ship-to address’s, or the marketplace’s own country when the address has none.',
      ],
      edgeCases: [
        'An order with no country at all is left out, and so is its share of the headline.',
        'A country can have orders but no sales if its sales are in a currency without a rate.',
      ],
      quirks: ['Orders and units count every store; sales are in the currency shown.'],
      source: ['The marketplace sales mart.'],
    },
    // reports/areas/listing_data.py · analytics mart_ads_daily.sql
    ads_by_country: {
      title: 'Advertising by country',
      shows: [
        'What the ads cost and brought in, country by country, in the window — on the map and in the table.',
      ],
      calculation: [
        'Clicks, spend and ad sales are summed per country; ACOS = spend ÷ ad sales, worked out from those sums.',
        'A country is the country of the store the ads ran for.',
        'Other currencies are converted by the backend at the latest stored exchange rate.',
      ],
      edgeCases: [
        'A country with ad sales of 0 has no ACOS.',
        'On a day the ad-group report is missing, only product ads are counted — a lower bound with no Sponsored Brands.',
      ],
      quirks: [
        'Filtering by brand drops Sponsored Brands spend, which has no SKU.',
        'The share column is each country’s share of the measure the map shows.',
      ],
      source: ['The daily ads mart (one row per ad per day, plus a remainder per ad group).'],
    },
    // dashboard/sections.py · dashboard/service.py
    movers: {
      title: 'Focused SKU ranking — biggest drops',
      shows: [
        'Your focused SKUs that got worse on the figure picked, last week against the week before.',
      ],
      calculation: [
        'This week = the 7 full days to yesterday (UTC); last week = the 7 before.',
        'Rank: the best rank on the last checked day of each week. Sales: units from non-cancelled lines. Ads: spend, clicks and sales per SKU; ACOS = spend ÷ ad sales.',
        'Worse means rank or ACOS went up, or anything else went down.',
      ],
      edgeCases: [
        'Only SKUs that got worse are listed; unchanged ones and ones missing a week are left out.',
        'At most 200 rows, biggest change first.',
      ],
      quirks: [
        'It ignores the date range: it is always the last two weeks.',
        'Ad figures leave out Sponsored Brands (they have no SKU), and are not converted.',
      ],
      source: ['Listing ranks, the sales mart and the daily ads mart.'],
    },
    no_sale: {
      title: 'No-sale focused SKUs',
      shows: [
        'Focused SKUs that sold nothing in the last 30 days, with their stock and ad clicks.',
      ],
      calculation: [
        '0 units in the 30 days to yesterday. Stock: sellable units at the marketplace now + available units in the store’s warehouses.',
      ],
      edgeCases: [
        'SKUs with no stock at all are still listed.',
        'Clicks show only if you can read advertising; otherwise they are 0.',
      ],
      quirks: ['It ignores the date range.', 'At most 200 rows, most clicks first.'],
      source: ['The sales mart, marketplace stock, warehouse stock and the daily ads mart.'],
    },
    stock_now: {
      title: 'On hand now',
      shows: ['Units in your warehouses and units Amazon can sell, as they are now.'],
      calculation: [
        'Warehouse: available units. Amazon: sellable units from each store’s latest report.',
      ],
      edgeCases: [
        'A store whose last Amazon report is more than 2 days behind the newest is left out until it reports again.',
      ],
      quirks: [
        'No date range — it is a snapshot.',
        'A pan-EU stock pool is counted once, not once per marketplace. Seller Flex stock is a lower bound.',
      ],
      source: ['The marketplace stock and warehouse stock marts.'],
    },
    aging: {
      title: 'Warehouse stock by brand and age',
      shows: ['Warehouse units by brand, split by how long they have been there.'],
      calculation: [
        'Age = whole days since the units were received. The bands add up to the total.',
      ],
      edgeCases: [
        'Stock with no receipt counts as unknown age; opening stock from before the migration has a band of its own.',
      ],
      quirks: ['No date range — it is a snapshot. Only a brand filter can narrow it.'],
      source: ['The warehouse stock mart.'],
    },
    cover: {
      title: 'Stock cover',
      shows: [
        'How many days your focused SKUs’ stock will last at their recent pace — the ones running out first.',
      ],
      calculation: [
        'Velocity = units sold in the last 30 days ÷ 30. Days of cover = (marketplace + warehouse stock) ÷ velocity.',
      ],
      edgeCases: [
        'SKUs that sold nothing are left out (their cover would be endless).',
        'The three bands count every SKU, but a SKU with more than 90 days falls in none of them.',
        'At most 200 rows, least cover first.',
      ],
      quirks: ['It ignores the date range.'],
      source: ['The sales mart, marketplace stock and warehouse stock.'],
    },
    suppressed: {
      title: 'Suppressed inventory',
      shows: ['Stock in your warehouses for SKUs that no marketplace lists.'],
      calculation: [
        'SKUs with available warehouse units and no listing anywhere in the organisation that is not deleted.',
      ],
      edgeCases: [
        'An inactive listing that is not deleted still counts as listed, so its stock is not here.',
        'At most 200 rows, most units first; the totals cover every SKU.',
      ],
      quirks: ['The listing check covers every store, even with a store or marketplace filter.'],
      source: ['The warehouse stock mart and the listing status mart.'],
    },
    slots: {
      title: 'Sponsored ad slots',
      shows: [
        'For each focused search term, how often your focused products held a sponsored slot.',
      ],
      calculation: [
        'Total = searches where one of your focused ASINs showed in the 5 sponsored or 5 organic results.',
        'Winning = it held a sponsored slot. Win rate = winning ÷ total.',
      ],
      edgeCases: [
        'Searches where none of your products showed are not counted at all.',
        'At most 200 rows, worst win rate first.',
      ],
      quirks: ['The last 7 days are a rolling 168 hours, not calendar days.'],
      source: ['The scraped search results for your focused search terms.'],
    },
    distressed: {
      title: 'Distressed ad groups',
      shows: ['Ad groups whose conversion fell sharply or whose ACOS is well over target.'],
      calculation: [
        'This week = the 7 days to the newest ad day; last week = the 7 before.',
        'Conversion = units ÷ clicks. A drop of 25% or more flags it.',
        'ACOS = spend ÷ sales. Above 1.5 × the ad group’s target flags it.',
        'Budget used = spend ÷ (daily budget × days with data).',
      ],
      edgeCases: [
        'Ad groups with no clicks this week are left out.',
        'Spend with no sales has no ACOS, so it is never flagged for ACOS.',
        'A target outside 1%–100% is ignored as a typo.',
      ],
      quirks: [
        'Amounts are in the currency typed on the ad group, not converted.',
        'At most 200 rows, highest ACOS against target first.',
      ],
      source: ['The ad-group daily data as ingested.'],
    },
    // tickets/repository.py · tickets/service.py
    worklist: {
      title: 'Your ticket worklist',
      shows: [
        'Your tickets by type: the open backlog now, and what you resolved and reviewed in the week picked.',
      ],
      calculation: [
        'Unassigned: tickets nobody owns that are pending or in progress.',
        'Assigned to me: yours in progress. Pending: yours waiting.',
        'Resolved: yours resolved in the week, whatever they are now. Reviewed: yours reviewed in the week.',
      ],
      edgeCases: [
        'Overdue = still open past created + the type’s ETA days. An ETA of 0 means no deadline.',
        'Notification and retired ticket types are not listed.',
      ],
      quirks: [
        'Unassigned is the whole pool you can see, not just yours.',
        'Days are in your time zone. No Sales & marketing filter applies.',
      ],
      source: ['Tickets.'],
    },
    done: {
      title: 'Done — month to date',
      shows: [
        'How many of your tickets you solved, reviewed or were assigned, day by day this month.',
      ],
      calculation: [
        'Solved by the day resolved; reviewed by the day completed (reviewed tickets only); assigned by the day assigned.',
      ],
      edgeCases: ['Every day is listed, with 0 where nothing happened.'],
      quirks: ['Days are in your time zone.'],
      source: ['Tickets.'],
    },
    snapshot: {
      title: 'My snapshot',
      shows: [
        'Four counts from the worklist and the chart: done today, done this month, overdue, and the unassigned pool.',
      ],
      calculation: [
        'Done today = resolved + reviewed today. Done this month = the chart’s total for the metric picked.',
      ],
      edgeCases: ['Overdue counts only open tickets.'],
      quirks: ['The unassigned pool is everyone’s, not just yours.'],
      source: ['Tickets.'],
    },
    threads: {
      title: 'Community forum',
      shows: ['The forum’s top threads, newest, most commented or most liked.'],
      calculation: ['Likes are the sum of the likes on a thread’s posts.'],
      edgeCases: [
        'Hidden and unapproved threads are left out.',
        'If the forum does not answer within 5 seconds, the card says it is not connected.',
      ],
      quirks: ['The top 10 of all time — the month picked applies to contributors only.'],
      source: ['The community forum’s own database.'],
    },
    contributors: {
      title: 'Most contributed',
      shows: ['The month’s 10 most active members, by posts and replies.'],
      calculation: ['Posts in the month (UTC), most first, then by name.'],
      edgeCases: ['Hidden, unapproved and event posts do not count.'],
      quirks: ['The month is in UTC, not your time zone.'],
      source: ['The community forum’s own database.'],
    },
  },
} as const

export type DashboardCardId = keyof typeof dashboardInfo.cards
